import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  Send,
  Sparkles,
  FileText,
  TrendingUp,
  BookOpen,
  Image as ImageIcon,
  Lightbulb
} from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function AIAssistant() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showAccessNotice, setShowAccessNotice] = useState(false);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    fetchLogo();
    loadWelcomeMessage();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => {
          setLogoUrl(data.value);
          setIsLoadingLogo(false);
        };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch (err) {
      setIsLoadingLogo(false);
    }
  };

  const loadWelcomeMessage = () => {
    const welcomeMessage: Message = {
      id: '1',
      type: 'assistant',
      content: "Hello! I'm your Workday AI Assistant. I can help you with:\n\n• Answering Workday configuration questions\n• Troubleshooting common issues\n• Best practices and recommendations\n• Module-specific guidance (HCM, Financials, Recruiting, etc.)\n• Technical documentation lookup\n\nHow can I assist you today?",
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
  };

  const quickActions = [
    { icon: <FileText className="w-5 h-5" />, text: 'Configuration help', query: 'How do I configure security groups in Workday?' },
    { icon: <TrendingUp className="w-5 h-5" />, text: 'Best practices', query: 'What are the best practices for Workday HCM implementation?' },
    { icon: <BookOpen className="w-5 h-5" />, text: 'Documentation', query: 'Where can I find documentation on Workday Recruiting?' },
    { icon: <Lightbulb className="w-5 h-5" />, text: 'Troubleshooting', query: 'How do I troubleshoot business process errors?' }
  ];

  const handleSend = async () => {
    if (!input.trim()) return;
    setShowAccessNotice(true);

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: getAIResponse(input),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const getAIResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('security') || lowerQuery.includes('configure')) {
      return "To configure security groups in Workday:\n\n1. Navigate to the Security Admin functional area\n2. Use the 'Create Security Group' task\n3. Choose the appropriate security group type (User-based, Role-based, etc.)\n4. Define membership criteria and permissions\n5. Test the security group in your implementation tenant\n\nRemember to follow the principle of least privilege and document all security configurations thoroughly.";
    }

    if (lowerQuery.includes('best practice') || lowerQuery.includes('hcm')) {
      return "Here are key best practices for Workday HCM implementation:\n\n1. **Planning Phase**: Define clear business requirements and success metrics\n2. **Data Migration**: Cleanse and validate data before migration\n3. **Configuration**: Use delivered functionality before customization\n4. **Testing**: Conduct thorough UAT with actual end users\n5. **Training**: Provide role-based training well before go-live\n6. **Change Management**: Communicate changes early and often\n7. **Support**: Establish a clear support structure post-implementation\n\nWould you like more details on any specific area?";
    }

    if (lowerQuery.includes('recruiting') || lowerQuery.includes('documentation')) {
      return "Workday Recruiting documentation can be found in:\n\n1. **Workday Community**: community.workday.com - comprehensive guides and user documentation\n2. **Implementation Guides**: Available in your tenant under 'Learning and Implementation'\n3. **Release Notes**: Check quarterly release notes for new features\n4. **Video Tutorials**: Workday Learning platform has step-by-step videos\n\nKey areas to review:\n• Job Requisition configuration\n• Candidate management workflows\n• Interview scheduling\n• Offer letter templates\n• Integrations with job boards\n\nNeed help with a specific Recruiting feature?";
    }

    if (lowerQuery.includes('error') || lowerQuery.includes('troubleshoot')) {
      return "For troubleshooting business process errors:\n\n1. **Check Process Logs**: Review the process instance for error details\n2. **Verify Permissions**: Ensure all required security permissions are in place\n3. **Data Validation**: Check if all required fields have valid data\n4. **Dependencies**: Verify all dependent processes completed successfully\n5. **Configuration**: Review business process definition and steps\n6. **Integration**: If applicable, check integration system logs\n\nCommon issues:\n• Missing required approvers\n• Invalid data in calculated fields\n• Timing issues with dependent processes\n• Security group membership gaps\n\nWhat specific error are you encountering?";
    }

    return "I can help you with Workday-related questions! I specialize in:\n\n• HCM configuration and best practices\n• Financials module guidance\n• Recruiting and talent management\n• Security and permissions\n• Business process troubleshooting\n• Integration support\n• Reporting and analytics\n\nPlease ask me a specific question about your Workday implementation or challenge!";
  };

  const handleQuickAction = (query: string) => {
    setInput(query);
    setShowAccessNotice(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img
                  src="/1.png"
                  alt="Perplexity"
                  className="h-40 w-auto object-contain"
                />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </div>
            <button
              onClick={() => navigate('/consultant/dashboard')}
              className="text-white hover:text-primary transition-colors flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center bg-black">
                <img src="/3.png" alt="AI Assistant" className="w-full h-full object-contain" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-2">AI Assistant</h1>
            <p className={`text-lg font-semibold mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
              For Workday Consultants and Professionals.
            </p>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Get instant help with Workday configurations, best practices, and troubleshooting.
            </p>
          </div>

          {/* Important Disclaimer */}
          <div className={`mb-8 p-6 rounded-xl border-2 ${
            theme === 'dark'
              ? 'bg-amber-900/20 border-amber-600/50'
              : 'bg-amber-50 border-amber-300'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 p-2 rounded-lg ${
                theme === 'dark' ? 'bg-amber-600/20' : 'bg-amber-100'
              }`}>
                <Lightbulb className={`w-6 h-6 ${
                  theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                }`} />
              </div>
              <div>
                <h3 className={`font-bold text-lg mb-2 ${
                  theme === 'dark' ? 'text-amber-400' : 'text-amber-900'
                }`}>
                  Important Notice
                </h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-amber-200' : 'text-amber-900'
                }`}>
                  Please note that the information provided by the Workday AI Assistant is general data from AI. Ensure that you edit and validate data to be specific to each client's tenant. Make sure data used for clients is validated and accurate. This AI Chat is meant to assist and help you save time. <strong>Remember to never use sensitive or personal client or worker data and always validate information.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action.query)}
                    className={`flex items-center p-4 rounded-xl transition-all duration-200 hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                        : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-sm'
                    }`}
                  >
                    <div className="p-3 bg-primary/10 rounded-lg mr-4">
                      <div className="text-primary">{action.icon}</div>
                    </div>
                    <span className="font-medium">{action.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Access Notice */}
          {showAccessNotice && (
            <div className="mb-6 p-5 rounded-xl border-2 border-[#40E0D0]/50 bg-[#40E0D0]/10 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden bg-black flex items-center justify-center">
                <img src="/3.png" alt="AI" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-[#40E0D0]' : 'text-[#008B8B]'}`}>
                  AI Assistant Access Required
                </p>
                <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  The AI Assistant is available to consultants on an <strong>active assignment</strong> or through our <strong>coming free trial</strong>. Please check back once your assignment is confirmed or stay tuned for the free trial launch.
                </p>
              </div>
              <button
                onClick={() => setShowAccessNotice(false)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-400 hover:text-white hover:border-gray-400'
                    : 'border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-500'
                }`}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Chat Container */}
          <div className={`rounded-xl overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {/* Messages */}
            <div className="h-[500px] overflow-y-auto p-6 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-xl p-4 ${
                      message.type === 'user'
                        ? 'bg-primary text-black'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {message.type === 'assistant' && (
                      <div className="flex items-center mb-2">
                        <Sparkles className="w-4 h-4 mr-2 text-primary" />
                        <span className="font-semibold text-sm">AI Assistant</span>
                      </div>
                    )}
                    <p className="whitespace-pre-line">{message.content}</p>
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' ? 'text-black/70' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className={`rounded-xl p-4 ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className={`border-t p-4 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex space-x-4">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything about Workday..."
                  rows={1}
                  className={`flex-1 px-4 py-3 rounded-lg border resize-none ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-primary`}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="bg-primary text-black font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className={`mt-6 p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-100'
          }`}>
            <p className={`text-sm text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              AI responses are for guidance only. Always verify configurations in your Workday tenant and consult official documentation for production implementations.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
